import { normalizeDateString } from 'common/services/format'

import type {
  AppointmentIssuesSet,
  MedicalIssue,
  ProcedureSearchRow,
  ReplaceAppointmentIssuesBody,
} from 'features/Schedule/api/medicalIssues.types'

import type { PickerEntry, PickerKey, ProblemEntry, ProcedureEntry } from './issuePicker.types'

// Same words as the OpenEMR picker (library/issue_picker_script.php), so both screens read alike.
export const PICKER_TEXT = {
  label: 'Problem List',
  placeholder: 'Type a problem or a procedure - or leave it empty',
  choosePatient: 'Choose a patient first.',
  noProblemsRecorded: 'No problems recorded for this patient.',
  allProblemsOnVisit: 'Every recorded problem is already on this visit.',
  nothingMatches: 'Nothing matches.',
  nothingRecorded: 'Nothing recorded for this visit yet.',
  proceduresFailed: 'Could not search procedures just now.',
  loadFailed: "Could not load this patient's problems.",
  problemsGroup: 'Problems',
  proceduresGroup: 'Procedures',
  problemKind: 'Problem',
  procedureKind: 'Procedure',
  lastSeen: 'last seen',
} as const

// Procedures search a large catalogue; problems filter locally from the first character.
export const PROCEDURE_SEARCH_MIN_CHARS = 2
export const PICKER_SEARCH_DEBOUNCE_MS = 200

const DATE_PART_COUNT = 3

// M/D/YYYY, no leading zeros, from an ISO date or datetime; '' for EMR zero-dates.
export const formatIssueUSDate = (iso: string | null | undefined): string => {
  const normalized = normalizeDateString(iso)
  if (!normalized) return ''
  const parts = normalized.slice(0, 10).split('-')
  if (parts.length !== DATE_PART_COUNT || parts.some((part) => Number.isNaN(Number(part)))) {
    return ''
  }
  const [year, month, day] = parts
  return `${Number(month)}/${Number(day)}/${year}`
}

export const getIssueLabel = (issue: MedicalIssue): string => {
  const label = `${issue.type_label || issue.type}: ${issue.title}`
  const lastSeen = formatIssueUSDate(issue.last_seen)
  return lastSeen ? `${label} — ${PICKER_TEXT.lastSeen} ${lastSeen}` : label
}

// "ICD10:L71.9" → "ICD10 L71.9", one shape wherever a code is shown.
export const formatCodeChip = (codeRef: string | null | undefined): string =>
  String(codeRef ?? '')
    .trim()
    .replace(':', ' ')

export const getPickerKey = (entry: Pick<PickerEntry, 'kind' | 'id'>): PickerKey =>
  `${entry.kind}:${entry.id}`

export const problemEntry = (issue: MedicalIssue): ProblemEntry => ({
  kind: 'problem',
  id: issue.id,
  label: getIssueLabel(issue),
  diagnosis: issue.diagnosis ?? '',
})

export const procedureEntry = (row: ProcedureSearchRow): ProcedureEntry => ({
  kind: 'procedure',
  id: row.code_id,
  label: row.label,
  code: row.code,
})

export const addPickerEntry = (chosen: PickerEntry[], entry: PickerEntry): PickerEntry[] => {
  const key = getPickerKey(entry)
  if (chosen.some((existing) => getPickerKey(existing) === key)) return chosen
  return [...chosen, entry]
}

export const removePickerEntry = (chosen: PickerEntry[], key: PickerKey): PickerEntry[] =>
  chosen.filter((entry) => getPickerKey(entry) !== key)

const splitDiagnosisCodes = (diagnosis: string | null | undefined): string[] =>
  String(diagnosis ?? '')
    .split(';')
    .map((code) => code.trim().toUpperCase())
    .filter(Boolean)

const chosenProcedureCodes = (chosen: PickerEntry[]): Set<string> =>
  new Set(
    chosen
      .filter((entry): entry is ProcedureEntry => entry.kind === 'procedure')
      .map((entry) => entry.code.trim().toUpperCase()),
  )

// One thing, one tag: a problem coded like a picked procedure is that procedure's twin.
const isTwinOfProcedure = (issue: MedicalIssue, procedureCodes: Set<string>): boolean =>
  splitDiagnosisCodes(issue.diagnosis).some((code) => procedureCodes.has(code))

// An empty term matches everything, which is what opens the list on click.
export const filterPatientProblems = (
  issues: MedicalIssue[],
  term: string,
  chosen: PickerEntry[],
): MedicalIssue[] => {
  const needle = term.trim().toUpperCase()
  const chosenKeys = new Set(chosen.map(getPickerKey))
  const procedureCodes = chosenProcedureCodes(chosen)
  return issues.filter((issue) => {
    if (chosenKeys.has(getPickerKey({ kind: 'problem', id: issue.id }))) return false
    if (isTwinOfProcedure(issue, procedureCodes)) return false
    const hay = `${issue.title ?? ''} ${issue.diagnosis ?? ''}`.toUpperCase()
    return hay.includes(needle)
  })
}

export const getEmptyProblemsMessage = (hasRecordedProblems: boolean): string =>
  hasRecordedProblems ? PICKER_TEXT.allProblemsOnVisit : PICKER_TEXT.noProblemsRecorded

// The procedure tag survives its problem twin: it carries the catalogue code the booking wants.
export const seedChosenEntries = (set: AppointmentIssuesSet): PickerEntry[] => {
  const procedureCodes = new Set(
    set.procedures.map((procedure) => procedure.code.trim().toUpperCase()),
  )
  const problems = set.issues
    .filter((issue) => issue.selected)
    .filter((issue) => !isTwinOfProcedure(issue, procedureCodes))
    .map(problemEntry)
  const procedures = set.procedures.map<ProcedureEntry>((procedure) => ({
    kind: 'procedure',
    id: procedure.id,
    label: procedure.title,
    code: procedure.code,
  }))
  return [...problems, ...procedures]
}

export const buildReplaceSetBody = (chosen: PickerEntry[]): ReplaceAppointmentIssuesBody => ({
  issue_ids: chosen.filter((entry) => entry.kind === 'problem').map((entry) => entry.id),
  procedure_code_ids: chosen.filter((entry) => entry.kind === 'procedure').map((entry) => entry.id),
})
